/* eslint-disable react/jsx-props-no-spreading */
import React, {
  forwardRef,
  useMemo,
} from 'react';
import {
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  useApiClient,
  useFetchImmutable,
} from '@marageti/z4-lib';

export interface PartnerOption {
  label: string;
  value: string;
}

export interface PartnerSelectorProps {
  error?: boolean | undefined;
  helperText?: string | undefined;
  label?: string | undefined;
  name?: string | undefined;
  onBlur?: (event: React.FocusEvent) => void,
  onChange: (event: React.SyntheticEvent, value: string) => void,
  value?: string | undefined;
}

const formatPartnerOptions = (partners: { [id: string]: string }) => {
  if (!partners || Object.keys(partners).length === 0) {
    return [{ label: 'All', value: 'all' }];
  }

  return [
    { label: 'All', value: 'all' },
    ...Object.entries(partners)
      .sort(([, partnerNameA], [, partnerNameB]) => partnerNameA.localeCompare(partnerNameB))
      .map(([partnerId, partnerName]) => ({ label: partnerName, value: partnerId })),
  ];
};

const PartnerSelector = forwardRef<HTMLDivElement, PartnerSelectorProps>(({
  error = false,
  helperText = undefined,
  label = undefined,
  name = undefined,
  onBlur = undefined,
  onChange,
  value = undefined,
}: PartnerSelectorProps, ref) => {
  const apiClient = useApiClient();
  const partners = useFetchImmutable<{ [partnerId: string]: string }>(apiClient.partnersClient.getPartnerNames, [], true);

  const currentValue = useMemo(() => {
    if (value && partners?.data?.[value]) {
      return { label: partners?.data?.[value], value };
    }
    return null;
  }, [partners?.data, value]);

  const partnerOptions = useMemo(() => {
    if (partners?.data) {
      return formatPartnerOptions(partners?.data);
    }
    return [{ label: 'All', value: 'all' }];
  }, [partners?.data]);

  const handleChange = (e: React.SyntheticEvent<Element, Event>, newValue: PartnerOption | null) => {
    onChange(e, newValue?.value || 'all');
  };

  const getTextFieldHelpers = () => {
    if (partners.error || error) {
      return {
        error: true,
        helperText: partners.error
          ? 'An error occurred while fetching partners'
          : helperText,
      };
    }

    return {
      error: false,
      helperText: '',
    };
  };

  return (
    <Autocomplete
      id={name || ''}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      getOptionKey={(o) => o.value}
      loading={partners.isLoading}
      onBlur={onBlur}
      onChange={handleChange}
      options={partners.data ? partnerOptions : []}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || 'Partner'}
          name={name}
          inputRef={ref}
          {...getTextFieldHelpers()}
        />
      )}
      fullWidth
      value={currentValue}
    />
  );
});

export default PartnerSelector;
